import * as React from "react";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  TypeableDatePicker,
  TypeableDatePickerProps,
} from "@volca/ui/components/ui/typeable-date-picker";
import { dayjs, generateTimeOptions } from "@volca/ui/lib/date";

interface DateTimePickerProps extends TypeableDatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export const DateTimePicker = ({
  value,
  onChange,
  label = "Date",
  placeholder = "mm/dd/yyyy",
  required,
  error,
  ...props
}: DateTimePickerProps) => {
  const [startTime, setStartTime] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("");
  const [startTimeSearch, setStartTimeSearch] = React.useState("");
  const [endTimeSearch, setEndTimeSearch] = React.useState("");
  const startTimeRef = React.useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [hasInteractedWithTime, setHasInteractedWithTime] =
    React.useState(false);

  // Update internal state when prop changes
  React.useEffect(() => {
    if (value) {
      setSelectedDate(value);
      // Only set times from value if user has not interacted with time fields
      if (!hasInteractedWithTime) {
        setStartTime(dayjs(value).format("h:mm a"));
        const endTimeDate = dayjs(value).add(30, "minute");
        setEndTime(endTimeDate.format("h:mm a"));
        setStartTimeSearch(dayjs(value).format("h:mm a"));
        setEndTimeSearch(endTimeDate.format("h:mm a"));
      }
    } else {
      setSelectedDate(null);
      setStartTime("");
      setEndTime("");
      setStartTimeSearch("");
      setEndTimeSearch("");
      setHasInteractedWithTime(false);
    }
  }, [value, hasInteractedWithTime]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);

    // Only update the full value if we have both date and time
    if (date && startTime) {
      try {
        // Parse the start time
        const [time, period] = startTime.split(" ");
        const [hours, minutes] = time.split(":");
        const isPM = period.toLowerCase() === "pm";
        let startHour = parseInt(hours);
        // Handle 12 AM/PM cases
        if (startHour === 12) {
          startHour = isPM ? 12 : 0;
        } else {
          startHour = isPM ? startHour + 12 : startHour;
        }

        onChange?.(
          dayjs(date).hour(startHour).minute(parseInt(minutes)).toDate(),
        );
      } catch (error) {
        console.error("Error parsing time:", error);
      }
    } else if (date === null) {
      // Handle case when date is cleared
      onChange?.(null);
    }

    // Focus the start time input after a short delay if it hasn't been set
    if (date && !startTime) {
      setTimeout(() => {
        startTimeRef.current?.focus();
      }, 100);
    }
  };

  const handleStartTimeChange = (value: string) => {
    setHasInteractedWithTime(true);
    setStartTime(value);
    setStartTimeSearch(value);

    try {
      // Parse the start time
      const [time, period] = value.split(" ");
      const [hours, minutes] = time.split(":");
      const isPM = period.toLowerCase() === "pm";
      let startHour = parseInt(hours);
      // Handle 12 AM/PM cases
      if (startHour === 12) {
        startHour = isPM ? 12 : 0;
      } else {
        startHour = isPM ? startHour + 12 : startHour;
      }

      // Parse the current end time if it exists
      let endHour = 0;
      let endMinute = 0;
      if (endTime) {
        const [endTimeStr, endPeriod] = endTime.split(" ");
        const [endHours, endMinutes] = endTimeStr.split(":");
        const isEndPM = endPeriod.toLowerCase() === "pm";
        const parsedEndHour = parseInt(endHours);
        // Handle 12 AM/PM cases for end time
        if (parsedEndHour === 12) {
          endHour = isEndPM ? 12 : 0;
        } else {
          endHour = isEndPM ? parsedEndHour + 12 : parsedEndHour;
        }
        endMinute = parseInt(endMinutes);
      }

      // Calculate total minutes for comparison
      const startTotalMinutes = startHour * 60 + parseInt(minutes);
      const endTotalMinutes = endHour * 60 + endMinute;

      // If end time is before start time or empty, set it to start time + 30 minutes
      if (!endTime || startTotalMinutes >= endTotalMinutes) {
        const newEndDate = dayjs()
          .hour(startHour)
          .minute(parseInt(minutes) + 30);
        const newEndTime = newEndDate.format("h:mm a");
        setEndTime(newEndTime);
        setEndTimeSearch(newEndTime);
      }

      // Update the date value if we have a valid date
      if (selectedDate) {
        onChange?.(
          dayjs(selectedDate)
            .hour(startHour)
            .minute(parseInt(minutes))
            .toDate(),
        );
      }
    } catch (error) {
      console.error("Error parsing time:", error);
    }
  };

  const handleEndTimeChange = (value: string) => {
    setHasInteractedWithTime(true);
    setEndTime(value);
    setEndTimeSearch(value);
  };

  const startTimeOptions = React.useMemo(() => generateTimeOptions(), []);

  const endTimeOptions = React.useMemo(() => {
    if (!startTime) {
      return generateTimeOptions();
    }
    return generateTimeOptions(startTime);
  }, [startTime]);

  return (
    <Stack className="w-full" align="start" gap={4}>
      <TypeableDatePicker
        value={selectedDate}
        onChange={handleDateChange}
        label={label}
        placeholder={placeholder}
        required={required}
        {...props}
      />

      <Stack className="w-full" align="start" gap={4} direction="row">
        <AutoComplete
          ref={startTimeRef}
          selectedValue={startTime}
          onSelectedValueChange={handleStartTimeChange}
          searchValue={startTimeSearch}
          onSearchValueChange={setStartTimeSearch}
          items={startTimeOptions}
          placeholder="Start time"
          label="Start time"
          required={required}
          fullWidth
        />
        <AutoComplete
          selectedValue={endTime}
          onSelectedValueChange={handleEndTimeChange}
          searchValue={endTimeSearch}
          onSearchValueChange={setEndTimeSearch}
          items={endTimeOptions}
          placeholder="End time"
          label="End time"
          required={required}
          fullWidth
        />
      </Stack>
      {error && (
        <Text variant="caption" tone="destructive">
          {error}
        </Text>
      )}
    </Stack>
  );
};
